import React, { Component, Fragment } from 'react';
import { computed, observable, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';
import { debounce } from 'lodash';

import { Button, ButtonToolbar, Modal } from 'react-bootstrap';
import ReactTable from 'react-table';
import checkboxHOC from 'react-table/lib/hoc/selectTable';

import ClientsClass from '../../../clients/ClientsClass';
import { AppConstants } from '../../../constants';
import { ILoanAccountContact, IReactTableColumn } from '../../../interfaces';
import { Form, Input } from '../../common-formsy';
import { SmartBool, TableRenderingUtils } from '../../../utils';
import { SelectInputComponent } from '../../../utils/TableRenderingUtils';

const { DEBOUNCE_DELAY, MAX_CONTACT_RESULT_DISPLAY } = AppConstants
  , { renderName, renderUnformatted } = TableRenderingUtils
  ;

const CheckboxTable = checkboxHOC(ReactTable);

// The pseudo-row that falls through to the form, as on the case add-contact page
const CREATE_NEW_ID = 'create-new'
  // Wide enough for the radio and nothing more, as on the case add-contact page's table
  , SELECT_COLUMN_WIDTH = 35
  ;

const COLUMNS: IReactTableColumn[] = [
  { Cell: renderName, className: 'col-name', Header: 'Name', id: 'fullName', minWidth: 140 },
  { accessor: 'email', Cell: renderUnformatted, Header: 'Email', minWidth: 140 },
  { accessor: 'phone_number', Cell: renderUnformatted, Header: 'Phone #', minWidth: 100 },
];

interface IProps {
  // The account being added to: contacts already on it are left out of the results, there being
  // nothing to add
  loanAccountId: string;
  onAssign: (contact: ILoanAccountContact) => Promise<void>;
  onClose: () => void;
  onCreateNew: (searchTerm: string) => void;
}

interface IInjected extends IProps {
  Clients: ClientsClass;
}

/**
 * The first step of adding a contact: search the funder's contacts by name, and either pick one to put
 * on this account or fall through to the form to create one. Same two questions the case add-contact
 * page asks, in a modal rather than a page of its own -- the account page's other writes are modals.
 */
@inject('Clients')
@autoBindMethods
@observer
class SearchContactsModal extends Component<IProps> {
  @observable private isLoadingSearch = new SmartBool();
  @observable private isSubmitting = new SmartBool();
  @observable private searchResults: ILoanAccountContact[] = [];
  @observable private searchTerm = '';
  @observable private selectedRow: null | string = null;
  // The term the rows on screen were found for, null before any search has landed. Compared against
  // what is typed to tell "nothing found" from "not searched yet" -- the two look the same in
  // searchResults, and reading an empty list as the former offers to create a contact that the search
  // about to run would have turned up.
  @observable private resultsTerm: null | string = null;
  // The term of the most recently started search, so a slower earlier one can be discarded when it
  // lands. Not observable: nothing renders it.
  private requestedTerm: null | string = null;
  private readonly debouncedSearch: (searchTerm: string) => void;

  constructor (props: IProps) {
    super(props);

    this.debouncedSearch = debounce(this.search, DEBOUNCE_DELAY);
  }

  private get injected () {
    return this.props as IInjected;
  }

  private get hasSearchQuery () {
    return this.searchTerm.trim() !== '';
  }

  // Whether the rows on screen answer the term in the box, rather than one typed before it
  private get hasSearchedForTerm () {
    return this.resultsTerm !== null && this.resultsTerm === this.searchTerm.trim();
  }

  private get hasNoSearchResults () {
    return this.hasSearchedForTerm && !this.searchResults.length;
  }

  private get isCreatingNew () {
    return this.hasNoSearchResults || this.selectedRow === CREATE_NEW_ID;
  }

  @computed
  private get rows () {
    // The create-new row renders through the same columns as a contact, so the fields it has nothing
    // to say for are blanked rather than left to show the empty-field placeholder.
    return [
      ...toJS(this.searchResults),
      { email: <br />, first_name: `Create new contact`, id: CREATE_NEW_ID, phone_number: <br /> },
    ];
  }

  private onSearchChange (searchTerm: string) {
    this.searchTerm = searchTerm;
    this.debouncedSearch(searchTerm);
  }

  // Not @action: a mobx 3 action does not extend across an await, so the assignments below it would
  // run outside the action anyway.
  private async search (searchTerm: string) {
    const { Clients } = this.injected
      , term = searchTerm.trim()
      ;

    this.requestedTerm = term;

    if (!term) {
      this.searchResults = [];
      this.resultsTerm = null;
      this.selectedRow = null;
      return;
    }

    try {
      // exclude_account leaves out the contacts already on this account: offering one would open a form
      // whose save changed nothing. Asked of the API rather than filtered here, the accounts a contact
      // is on not being served -- see ILoanAccountContact.
      const response = await this.isLoadingSearch.until(
        Clients.loanAccountContacts.list({
          params: { exclude_account: this.props.loanAccountId, search: term },
        }),
      );

      /**
       * Another search started while this one was in flight, so these rows answer a term the user has
       * moved past -- the debounce spaces the requests out but says nothing about the order they come
       * back in, and a slow one can land after a quick one. Dropping it also leaves alone any row picked
       * out of the newer results. c.f. LoanAccountPage, which discards a stale account the same way.
       */
      if (this.requestedTerm !== term) { return; }

      this.searchResults = response.results.slice(0, MAX_CONTACT_RESULT_DISPLAY);
      this.resultsTerm = term;
      // A row picked out of the list this replaces isn't necessarily in it, and submitting would then
      // have nothing to assign
      this.selectedRow = null;
    }
    catch (error) {
      // Transport has already toasted the failure; swallowing it here avoids a second toast.
      // tslint:disable-next-line no-console
      console.error('Error searching the account contacts:', error);
    }
  }

  private async onSubmit () {
    const { onAssign, onCreateNew } = this.props;

    if (this.isCreatingNew) {
      onCreateNew(this.searchTerm);
      return;
    }

    const contact = this.searchResults.find(result => result.id === this.selectedRow);

    // istanbul ignore next: the button is disabled until a row is selected
    if (!contact) { return; }

    await this.isSubmitting.until(onAssign(contact));
  }

  private isRowSelected (key: string) {
    return this.selectedRow === key;
  }

  private toggleRowSelection (key: string) {
    this.selectedRow = key;
  }

  private renderSearchResults () {
    if (!this.hasSearchQuery) { return null; }

    /**
     * Everything between typing and the results landing -- the debounce, then the request -- with
     * nothing on screen to keep up. Rows found for an earlier term stay put instead: swapping a table of
     * matches for a single line collapses the modal and reopens it a debounce later, on every letter
     * typed. c.f. TabCompliance's refresh, which holds its tables up through a re-read for the same
     * reason.
     *
     * This message and the one below are each one line, and .section-search keeps a line's worth of
     * space whichever is up -- including neither -- so the short states don't resize the modal either.
     */
    if (!this.hasSearchedForTerm && !this.searchResults.length) {
      return <div className='search-message'>Loading...</div>;
    }

    if (this.hasNoSearchResults) {
      return <div className='search-message'>No existing contacts found. Continue to create a new one.</div>;
    }

    return (
      <Fragment>
        <p className='info'>
          It looks like this contact already exists.<br />
          Select the right one below, or add it as a new contact.
        </p>
        <CheckboxTable
          className='-selectable -list -single-line -spaced-rows'
          columns={COLUMNS}
          data={this.rows}
          isSelected={this.isRowSelected}
          keyField='id'
          minRows={0}
          pageSize={this.rows.length}
          resizable={false}
          SelectInputComponent={SelectInputComponent}
          selectedRows={this.selectedRow}
          selectType='radio'
          selectWidth={SELECT_COLUMN_WIDTH}
          showPagination={false}
          sortable={false}
          toggleSelection={this.toggleRowSelection}
        />
      </Fragment>
    );
  }

  public render () {
    const { onClose } = this.props
      /**
       * There has to be something to submit: a name to search under, and then either a row picked out of
       * the results or a search that came back without the contact in it. Until then the button is a
       * dead end -- it has nothing to assign and nothing to say does not exist yet.
       *
       * The first clause covers an empty box, including one cleared after a row was picked: the results
       * are hidden along with the selection, so submitting would assign a contact that is no longer on
       * screen.
       */
      , isSubmitDisabled = !this.hasSearchQuery
        || (!this.isCreatingNew && !this.selectedRow)
        || this.isSubmitting.isTrue
      ;

    return (
      <Modal show className='modal-search-loan-account-contacts' onHide={onClose}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Contact</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form className='modal-form-style'>
            <h5>Contact's Full Name</h5>
            <Input
              field='contactName'
              name='contactName'
              onChange={this.onSearchChange}
              placeholder='Full Name'
              value={this.searchTerm}
            />
            <div className='section-search'>{this.renderSearchResults()}</div>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <ButtonToolbar className='pull-right'>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              bsStyle='primary'
              className='btn-add-contact-next'
              disabled={isSubmitDisabled}
              onClick={this.onSubmit}
            >
              {this.isCreatingNew ? 'Create new contact' : 'Next'}
            </Button>
          </ButtonToolbar>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default SearchContactsModal;
